import { call, put } from 'redux-saga/effects';
import { ApiError } from 'store/api/types';
import { CheckContactRequestAction } from '../types';
import { checkResponse } from 'store/utils';

import {
  CheckContactResponse,
  GetContactResponse,
  checkContact,
  getContact,
} from '../api';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import { MessageStates } from 'containers/MessageBox';
import { checkContactFailure, checkContactSuccess } from '../actions';
import { logoutRequest } from 'store/auth/actions';
import { renderErrorMessage } from 'utils/render-error-message';
import i18n from 'i18next';

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* checkContactSaga(action: CheckContactRequestAction) {
  try {
    let err: ApiError | null;
    const { contactUuid, connectionId } = action.payload;

    // 1. Trigger the VIES check on the backend.
    const checkResponseData: CheckContactResponse = yield call(
      checkContact,
      contactUuid,
      connectionId
    );
    err = checkResponse(checkResponseData?.responseMessage);
    if (err) throw err;

    // 2. Re-read the contact from the source of truth. The POST /check
    //    endpoint has been observed to return stale fields for the contact
    //    (e.g. the previous viesStatus / lastViesCheck) which then propagates
    //    into the contacts list. The GET endpoint reflects the persisted state
    //    after the check has been processed, so we prefer it for the store
    //    update. If this follow-up read fails we still consider the check
    //    successful (the backend already accepted it) and fall back to the
    //    POST response, warning the user that the row may need a manual
    //    refresh to show the latest values.
    let viesChecks = checkResponseData.data.viesChecks;
    let contact = checkResponseData.data.contact;
    let usedFreshData = false;

    try {
      const fetchResponseData: GetContactResponse = yield call(
        getContact,
        connectionId,
        contactUuid
      );
      const fetchErr = checkResponse(fetchResponseData?.responseMessage);
      if (!fetchErr && fetchResponseData?.data?.contact) {
        viesChecks = fetchResponseData.data.viesChecks || viesChecks;
        contact = fetchResponseData.data.contact;
        usedFreshData = true;
      } else if (fetchErr) {
        throw fetchErr;
      }
    } catch (fetchError: any) {
      const warning: MessageData = {
        title: i18n.t('notifications.heads-up'),
        description: i18n.t('notifications.the-vat-check-completed-but-we-could-not-retrieve-the-latest-con'),
        type: MessageStates.WARNING,
      };
      yield put(turnMessageOn(warning));
    }

    yield put(checkContactSuccess(viesChecks, contact));

    // 3. Success toast is shown after the store has been updated with the
    //    freshest data we could obtain, so the toast and the row update land
    //    together for the user.
    if (usedFreshData) {
      const message: MessageData = {
        title: i18n.t('common.success'),
        description: i18n.t('notifications.contact-was-checked'),
        type: MessageStates.SUCCESS,
      };
      yield put(turnMessageOn(message));
    }
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(checkContactFailure());
  }
}
